import { IBalusterGeometry, IRailingConfig, IRailingGeometry, IStairConfig, IVec3 } from '@stair-craft/common/util';

import { balusterCount } from '../shared-baluster-count/baluster-count';
import { openingRectOf, placeUpperRailingsOnSlab } from '../shared-upper-railings-on-slab/upper-railings-on-slab';

/**
 * Builds one upper-floor railing segment from an edge, or null when the edge is too short to rail.
 * Extracted from `generateUpperFloorRailing` to keep its complexity low.
 */
function buildEdgeRailing(edge: [number, number, number, number], y: number, rc: IRailingConfig, midX: number): IRailingGeometry | null {
    const [x1, z1, x2, z2]: [number, number, number, number] = edge;
    const edgeLength: number = Math.sqrt((x2 - x1) ** 2 + (z2 - z1) ** 2);
    if (edgeLength < 100) {
        return null;
    }

    const posts: IBalusterGeometry[] = [
        { position: { x: x1, z: z1, y }, height: rc.height, rotation: 0 },
        { position: { x: x2, z: z2, y }, height: rc.height, rotation: 0 },
    ];

    const slots: number = balusterCount(edgeLength, rc);
    const balusters: IBalusterGeometry[] = [];
    for (let i: number = 1; i < slots; i++) {
        const t: number = i / slots;
        balusters.push({
            position: { x: x1 + (x2 - x1) * t, z: z1 + (z2 - z1) * t, y },
            height: rc.height,
            rotation: 0,
        });
    }

    const handrail: { points: IVec3[]; width: number; height: number } = {
        points: [
            { x: x1, y: y + rc.height, z: z1 },
            { x: x2, y: y + rc.height, z: z2 },
        ],
        width: rc.handrailWidth,
        height: rc.handrailHeight,
    };

    const verticalSide: 'left' | 'right' = x1 <= midX ? 'left' : 'right';
    const side: 'left' | 'right' = x1 === x2 ? verticalSide : 'left';

    return { side, balusters, handrail, posts, kind: 'upper', balusterWidth: rc.balusterWidth, postWidth: rc.postWidth };
}

/**
 * Which opening edge is the stair-exit side vs. the far side, and whether each sits against a room wall.
 * Extracted from `generateUpperFloorRailing` to keep its complexity low.
 */
function resolveExitSides(
    isRight: boolean,
    ox1: number,
    ox2: number,
    rw: number
): { exitX: number; nonExitX: number; exitAgainstWall: boolean; nonExitAgainstWall: boolean } {
    const exitX: number = isRight ? ox2 : ox1;
    const nonExitX: number = isRight ? ox1 : ox2;
    const exitAgainstWall: boolean = isRight ? ox2 >= rw : ox1 <= 0;
    const nonExitAgainstWall: boolean = isRight ? ox1 <= 0 : ox2 >= rw;
    return { exitX, nonExitX, exitAgainstWall, nonExitAgainstWall };
}

export function generateUpperFloorRailing(config: IStairConfig): IRailingGeometry[] {
    const rc: IRailingConfig = config.railing;
    const y: number = config.floorHeight + config.floorThickness;
    const railings: IRailingGeometry[] = [];
    const isRight: boolean = config.turnDirection === 'right';

    // Anchor in WORLD coords (relative to room origin)
    const ox1: number = config.openingX;
    const ox2: number = config.openingX + config.openingWidth;
    const oz1: number = config.openingZ;
    const oz2: number = config.openingZ + config.openingLength;
    const sw: number = config.stairWidth;

    const rw: number = config.roomWidth;
    const rl: number = config.roomLength;

    // Exit zone (in world Z): where flight 2's last step emerges through the slab edge.
    // For default L-shape positioning, flight 2 occupies world z = [oz2 - sw, oz2].
    // The exit-side rail must NOT cross this zone, otherwise it physically blocks the stair head.
    const exitZWorldStart: number = oz2 - sw;

    const { exitX, nonExitX, exitAgainstWall, nonExitAgainstWall } = resolveExitSides(isRight, ox1, ox2, rw);

    const edges: [number, number, number, number][] = [];

    if (oz1 > 0) {
        edges.push([ox1, oz1, ox2, oz1]);
    }
    if (oz2 < rl) {
        edges.push([ox1, oz2, ox2, oz2]);
    }
    // Exit side edge — STOP at exit zone start so the rail doesn't block the stair exit
    if (!exitAgainstWall && oz1 < exitZWorldStart) {
        edges.push([exitX, oz1, exitX, exitZWorldStart]);
    }
    if (!nonExitAgainstWall) {
        edges.push([nonExitX, oz1, nonExitX, oz2]);
    }

    const midX: number = (ox1 + ox2) / 2;
    for (const edge of edges) {
        const railing: IRailingGeometry | null = buildEdgeRailing(edge, y, rc, midX);
        if (railing) {
            railings.push(railing);
        }
    }

    placeUpperRailingsOnSlab(railings, openingRectOf(config), config.railing.balusterSpacing, {
        width: config.roomWidth,
        length: config.roomLength,
    });
    return railings;
}
